import { Link, useParams } from "react-router-dom";
import { CalendarPlus, CalendarDays, Clock, MapPin, Users, Accessibility, ClipboardList, Building2, ArrowLeft } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { CategoryPill, DemoBadge, Img, Prose } from "@/components/common";
import FavButton from "@/components/FavButton";
import ShareBar from "@/components/ShareBar";
import ActivityCard, { LiveTag, StatusTag } from "@/components/ActivityCard";
import NotFound from "@/pages/NotFound";
import useTitle from "@/lib/useTitle";
import { track } from "@/lib/api";
import { dayOf, downloadICS, endOf, fmtDay, fmtDuration, isLive, timeOf } from "@/lib/time";

function Fact({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3 py-3 border-b border-black/5">
      <Icon className="h-5 w-5 mt-0.5 text-[var(--leaf)] shrink-0" aria-hidden />
      <div><dt className="eyebrow text-slate-500">{label}</dt><dd className="font-medium mt-0.5">{children}</dd></div>
    </div>
  );
}

const gcal = (a, place) => {
  const f = (s) => s.replace(/[-:]/g, "") + "00";
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(a.title)}&dates=${f(a.start)}/${f(endOf(a))}&ctz=Europe/Lisbon&location=${encodeURIComponent(place)}&details=${encodeURIComponent(window.location.href)}`;
};

export default function Atividade() {
  const { slug } = useParams();
  const site = useSite();
  const a = site.data.activities.find((x) => x.slug === slug);
  useTitle(a?.title || "Atividade");
  if (!a) return <NotFound />;
  const cat = site.cats[a.category_id];
  const space = site.spaces[a.space_id];
  const orgs = (a.organization_ids || []).map((id) => site.orgs[id]).filter(Boolean);
  const people = (a.people_ids || []).map((id) => site.people[id]).filter(Boolean);
  const event = site.events[a.event_id];
  const related = site.data.activities.filter((x) => x.category_id === a.category_id && x.id !== a.id).slice(0, 3);
  const place = site.placeOf(a);

  return (
    <article data-testid="activity-page">
      <div className="relative bg-[var(--ink)] text-white overflow-hidden">
        <Img src={a.image} position={a.image_position} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] to-transparent" />
        <div className="relative container-x pt-10 pb-14 sm:pt-16 sm:pb-20">
          <Link to="/programacao" className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-white min-h-[44px]" data-testid="activity-back-link"><ArrowLeft className="h-4 w-4" aria-hidden />Programação</Link>
          <div className="mt-6 flex flex-wrap items-center gap-2">{isLive(a, site.now) && <LiveTag />}<StatusTag act={a} /><CategoryPill cat={cat} size="md" /><DemoBadge show={a.demo} /></div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95] mt-5 max-w-4xl" data-testid="activity-title">{a.title}</h1>
          <p className="mt-5 font-mono-x text-lg sm:text-xl text-[var(--sun)]" data-testid="activity-when">{fmtDay(dayOf(a.start))} · {timeOf(a.start)}–{timeOf(endOf(a))}</p>
        </div>
      </div>

      <div className="container-x py-12 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          {a.change_note && <p className="mb-6 rounded-2xl bg-amber-100 border border-amber-300 p-4 font-semibold text-amber-900" data-testid="activity-change-note">Alteração: {a.change_note}</p>}
          {a.summary && <p className="font-display text-2xl font-bold leading-snug mb-6">{a.summary}</p>}
          <Prose text={a.description} className="text-base md:text-lg" />
          <div className="mt-10 flex flex-wrap gap-3">
            <FavButton act={a} variant="full" />
            <button onClick={() => { downloadICS([a], site.placeOf, a.slug); track("calendar", "", a.id); }} className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-[var(--leaf)] text-white font-semibold" data-testid="activity-add-calendar-button"><CalendarPlus className="h-5 w-5" aria-hidden />Adicionar ao calendário</button>
            <a href={gcal(a, place)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-12 px-5 rounded-full border-2 border-black/15 font-semibold" data-testid="activity-google-calendar-link">Google Calendar</a>
          </div>
          <div className="mt-8"><p className="eyebrow text-slate-500 mb-3">Partilhar</p><ShareBar title={a.title} text={a.summary} path={`/atividade/${a.slug}`} /></div>
        </div>
        <aside className="lg:col-span-5">
          <dl className="bg-white rounded-3xl border border-black/5 p-6">
            <Fact icon={CalendarDays} label="Data">{fmtDay(dayOf(a.start))}</Fact>
            <Fact icon={Clock} label="Horário e duração">{timeOf(a.start)}–{timeOf(endOf(a))} · {fmtDuration(a.duration)}</Fact>
            <Fact icon={MapPin} label="Local">{space ? <Link className="underline" to={`/mapa?espaco=${space.slug}`} data-testid="activity-space-link">{space.name}</Link> : place}</Fact>
            {orgs.length > 0 && <Fact icon={Building2} label="Organização">{orgs.map((o, i) => <span key={o.id}>{i > 0 && ", "}<Link className="underline" to={`/organizacoes/${o.slug}`} data-testid={`activity-org-link-${o.slug}`}>{o.name}</Link></span>)}</Fact>}
            {people.length > 0 && <Fact icon={Users} label="Participantes">{people.map((p) => p.name).join(", ")}</Fact>}
            {a.audience?.length > 0 && <Fact icon={Users} label="Público-alvo">{a.audience.join(", ")}</Fact>}
            {a.accessibility?.length > 0 && <Fact icon={Accessibility} label="Acessibilidade">{a.accessibility.join(", ")}</Fact>}
            <Fact icon={ClipboardList} label="Inscrição">
              {a.registration_required ? <>{a.registration_info || "Inscrição necessária."}{a.registration_url && <> <a href={a.registration_url} className="underline" target="_blank" rel="noreferrer">Inscrever-me</a></>}</> : "Entrada livre, sem inscrição."}
            </Fact>
            {event && <Fact icon={CalendarDays} label="Iniciativa">{<Link className="underline" to={`/semana/${event.slug}`}>{event.title}</Link>}</Fact>}
          </dl>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="container-x pb-20" aria-labelledby="rel">
          <h2 id="rel" className="font-display text-3xl font-extrabold mb-6">Também em {cat?.name}</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{related.map((r) => <ActivityCard key={r.id} act={r} />)}</div>
        </section>
      )}
    </article>
  );
}
